/**
 * The sign-in dialog, and a new staff member's first visit.  CA-17, CA-129.
 *
 * - The server's refusal is shown VERBATIM: it is identical for a wrong
 *   password, an unknown address and a disabled account on purpose, and a
 *   friendlier sentence here would undo that.
 * - A failed request is not a failed login: "Får ikke kontakt med serveren".
 * - A dialog, modal, named by its heading; the panel behind is `inert`
 *   (App does that) and Tab cycles inside the form (A11Y-ADM-06).
 * - The invite token is taken out of the address bar once used: it must not
 *   stay in the history or a bookmark.
 * - «Glemt passord?» (owner, 2026-10-25) asks the server to e-mail a one-time
 *   link (POST /admin/auth/forgot). Its answer is the same for every address and
 *   is shown verbatim, for the same reason as the sign-in refusal. The link opens
 *   `Invite` below, which is the same for a first password and a new one.
 * - «Logg inn med Vipps» (owner, 2026-11-01) under the password form, only when the server says the mosque's Vipps Login
 *   is set up (GET /admin/auth/methods). It works only for a staff member who linked their own Vipps under Min konto;
 *   the code Vipps comes back with is spent here with this tab's binding (lib/vipps-login.ts). The password form is as
 *   it was.
 */
import React, { useEffect, useRef, useState } from 'react';
import { publicPost, setSession } from '../lib/api';
import { exchangeVipps, LOGIN_ERROR, startVipps, vippsOffered, type VippsReturn } from '../lib/vipps-login';
import { Mark } from './ui';

function trapTab(ev: React.KeyboardEvent<HTMLDivElement>) {
  if (ev.key !== 'Tab') return;
  const f = [...ev.currentTarget.querySelectorAll<HTMLElement>('input, button, a[href]')].filter((x) => !(x as HTMLButtonElement).disabled && x.offsetParent !== null);
  if (!f.length) return;
  const first = f[0], last = f[f.length - 1];
  if (ev.shiftKey && document.activeElement === first) { ev.preventDefault(); last.focus(); }
  else if (!ev.shiftKey && document.activeElement === last) { ev.preventDefault(); first.focus(); }
}

/** «Glemt passord?»: the address, then the server's one answer. */
function Forgot({ email: start, onBack }: { email: string; onBack: () => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState('');
  const email = useRef<HTMLInputElement>(null);
  useEffect(() => { setTimeout(() => email.current?.focus(), 30); }, []);
  async function submit(ev: React.FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    const address = String(new FormData(ev.currentTarget).get('email') || '').trim();
    setErr(''); setBusy(true);
    const r = await publicPost('/admin/auth/forgot', { email: address });
    setBusy(false);
    if (!r.ok) { setErr((r.body.message_no || 'Det gikk ikke.') + (r.body.retryAfterSeconds ? ` (${r.body.retryAfterSeconds} s)` : '')); return; }
    setDone(r.body.message_no || 'Sjekk e-posten din.');
  }
  return (
    <form className="masjid-login__box" autoComplete="on" onSubmit={submit}>
      <div className="masjid-login__mark"><Mark width={38} height={54} /></div>
      <h2 id="masjid-login-title">Glemt passord</h2>
      <p className="masjid-login__note">Skriv e-postadressen du logger inn med. Du får en lenke der du velger et nytt passord.</p>
      <label>E-postadresse<input ref={email} type="email" name="email" autoComplete="username" defaultValue={start} required /></label>
      {!done && <button type="submit" disabled={busy}>{busy ? 'Sender …' : 'Send lenke'}</button>}
      <p className="masjid-login__err" role="alert">{err}</p>
      {done && <p className="masjid-login__ok" role="status">{done}</p>}
      <button type="button" className="masjid-login__link" onClick={onBack}>Tilbake til innlogging</button>
    </form>
  );
}

export function SignIn({ note, vipps }: { note?: string | null; vipps?: VippsReturn | null }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [forgot, setForgot] = useState(false);
  const [offerVipps, setOfferVipps] = useState(false);
  const typed = useRef('');
  const spent = useRef(false);
  const email = useRef<HTMLInputElement>(null);
  useEffect(() => { setTimeout(() => email.current?.focus(), 30); }, []);
  useEffect(() => { void vippsOffered().then(setOfferVipps); }, []);
  // Back from Vipps: a one-time code to spend, or the reason it did not work. Once, when the form opens.
  useEffect(() => {
    if (!vipps || spent.current) return;
    spent.current = true;   // once, even where React runs an effect twice (development)
    if (vipps.kind === 'loginError') { setErr(LOGIN_ERROR[vipps.reason] || LOGIN_ERROR.failed); return; }
    if (vipps.kind !== 'login') return;
    setBusy(true);
    void exchangeVipps(vipps.code).then((r) => {
      setBusy(false);
      if (!r.ok) { setErr(r.status === 400 || r.status === 429 ? (r.body.message_no || LOGIN_ERROR.failed) : LOGIN_ERROR.failed); return; }
      setSession({ token: r.body.token, user: r.body.user, permissions: r.body.permissions || [], expiresAt: r.body.expiresAt });
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  async function withVipps() {
    setErr(''); setBusy(true);
    const e = await startVipps('login');
    if (e) { setBusy(false); setErr(e); }
  }
  async function submit(ev: React.FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    const f = ev.currentTarget;
    const data = new FormData(f);
    setErr(''); setBusy(true);
    const r = await publicPost('/admin/auth/login', { email: String(data.get('email') || '').trim(), password: String(data.get('password') || '') });
    setBusy(false);
    if (!r.ok) {
      setErr((r.body.message_no || 'Kunne ikke logge inn.') + (r.body.retryAfterSeconds ? ` (${r.body.retryAfterSeconds} s)` : ''));
      return;
    }
    setSession({ token: r.body.token, user: r.body.user, permissions: r.body.permissions || [], expiresAt: r.body.expiresAt });
  }
  return (
    <div id="masjid-login" style={{ display: 'flex' }} role="dialog" aria-modal="true" aria-labelledby="masjid-login-title" onKeyDown={trapTab}>
      {forgot ? <Forgot email={typed.current} onBack={() => setForgot(false)} /> :
      <form className="masjid-login__box" autoComplete="on" onSubmit={submit}>
        <div className="masjid-login__mark"><Mark width={38} height={54} /></div>
        <h2 id="masjid-login-title">Logg inn</h2>
        <p className="masjid-login__note">{note || ''}</p>
        <label>E-postadresse<input ref={email} type="email" name="email" autoComplete="username" required /></label>
        <label>Passord<input type="password" name="password" autoComplete="current-password" required /></label>
        <button type="submit" disabled={busy}>{busy ? 'Logger inn …' : 'Logg inn'}</button>
        <p className="masjid-login__err" role="alert">{err}</p>
        <button type="button" className="masjid-login__link" onClick={() => { typed.current = email.current?.value.trim() || ''; setForgot(true); }}>Glemt passord?</button>
        {offerVipps && <>
          <p className="masjid-login__or" aria-hidden="true">eller</p>
          <button type="button" className="masjid-login__vipps" disabled={busy} onClick={() => { void withVipps(); }}>Logg inn med Vipps</button>
        </>}
        <p className="masjid-login__foot">Du blir logget ut etter 24 timer.</p>
      </form>}
    </div>
  );
}

export function Invite({ token, onDone }: { token: string; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const pw = useRef<HTMLInputElement>(null);
  useEffect(() => { setTimeout(() => pw.current?.focus(), 30); }, []);
  async function submit(ev: React.FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    const d = new FormData(ev.currentTarget);
    const password = String(d.get('password') || ''), again = String(d.get('again') || '');
    setErr('');
    if (password !== again) { setErr('Passordene er ikke like.'); return; }
    setBusy(true);
    const r = await publicPost('/admin/auth/accept-invite', { token, password });
    setBusy(false);
    if (!r.ok) { setErr(r.body.message_no || 'Passordet ble ikke lagret.'); return; }
    const u = new URL(location.href); u.searchParams.delete('invite');
    history.replaceState(null, '', u.toString());
    setSession({ token: r.body.token, user: r.body.user, permissions: r.body.permissions || [], expiresAt: r.body.expiresAt });
    onDone();
  }
  return (
    <div id="masjid-login" style={{ display: 'flex' }} role="dialog" aria-modal="true" aria-labelledby="masjid-invite-title" onKeyDown={trapTab}>
      <form className="masjid-login__box" autoComplete="on" onSubmit={submit}>
        <div className="masjid-login__mark"><Mark width={38} height={54} /></div>
        <h2 id="masjid-invite-title">Velg passord</h2>
        <p className="masjid-login__note">Velg et passord på minst 12 tegn — gjerne noen ord etter hverandre. Ingen andre ser det.</p>
        <label>Nytt passord<input ref={pw} type="password" name="password" autoComplete="new-password" minLength={12} required /></label>
        <label>Gjenta passordet<input type="password" name="again" autoComplete="new-password" required /></label>
        <button type="submit" disabled={busy}>{busy ? 'Lagrer …' : 'Lagre og logg inn'}</button>
        <p className="masjid-login__err" role="alert">{err}</p>
        <p className="masjid-login__foot">Lenken virker én gang.</p>
      </form>
    </div>
  );
}
